// components/home/listings/land/Land.jsx
import React, { useState, useEffect } from 'react';
import PropertyCard from '../property/PropertyCard';
import SearchBar from '../hotel/SearchBar';
import FilterModal from '../hotel/FilterModal';
import { getAllProperties } from '../../../../api/user/property/property';
import Loader from '../../../common/Loader';
import Error from '../../../common/Error';
import useGeolocation from '../../../../hooks/useGeolocation';
import { useTranslation } from 'react-i18next';
import { parseSearchQuery, matchesParsedQuery } from '../../../../utils/parseSearchQuery';
import '../property/PropertyPage.css';

const EMPTY_FILTERS = {
    name: '',
    city: '',
    location: '',
    minPrice: '',
    maxPrice: '',
    rating: '',
    amenities: []
};

const Land = () => {
    const { t } = useTranslation();
    const [properties, setProperties] = useState([]);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [nearbyProperties, setNearbyProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingNearby, setLoadingNearby] = useState(false);
    const [error, setError] = useState(null);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const location = useGeolocation();
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    useEffect(() => {
        const fetchProperties = async () => {
            setLoading(true);
            try {
                const response = await getAllProperties();
                const all = response.properties || [];
                const landList = all.filter(p => p?.data?.type == 'land');
                setProperties(all);
                setFilteredProperties(landList);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchProperties();
    }, []);

    useEffect(() => {
        if (!location.loaded || !location.coordinates?.lat || !location.coordinates?.lng) return;
        const fetchNearby = async () => {
            setLoadingNearby(true);
            try {
                const response = await getAllProperties({
                    latitude: location.coordinates.lat,
                    longitude: location.coordinates.lng,
                    radius: 50000
                });
                setNearbyProperties(
                    (response.properties || []).filter(p => p?.data?.type == 'land')
                );
            } catch (err) {
                console.error('Nearby properties error:', err.message);
            } finally {
                setLoadingNearby(false);
            }
        };
        fetchNearby();
    }, [location.loaded, location.coordinates?.lat, location.coordinates?.lng]);

    const landBase = properties.filter(p => p?.data?.type == 'land');

    // La main d'indice ne s'affiche que sur la première carte visible de la page
    const firstIsNearby = location.loaded && !location.error && nearbyProperties.length > 0;

    const applyFilters = (term, appliedFilters) => {
        let filtered = [...landBase];

        // Smart search
        if (term.trim()) {
            const parsed = parseSearchQuery(term);
            filtered = filtered.filter(p =>
                matchesParsedQuery(p, parsed, {
                    name: (p) => p.data?.name || '',
                    location: (p) => p.data?.location || '',
                    description: (p) => p.data?.description || p.data?.name || '',
                    category: () => 'land',
                })
            );
        }

        if (appliedFilters.name) {
            filtered = filtered.filter(p =>
                p.data?.name?.toLowerCase().includes(appliedFilters.name.toLowerCase())
            );
        }

        if (appliedFilters.city) {
            filtered = filtered.filter(p =>
                p.data?.location?.toLowerCase().includes(appliedFilters.city.toLowerCase())
            );
        }

        if (appliedFilters.location) {
            filtered = filtered.filter(p =>
                p.data?.location?.toLowerCase().includes(appliedFilters.location.toLowerCase())
            );
        }

        if (appliedFilters.minPrice || appliedFilters.maxPrice) {
            filtered = filtered.filter(p => {
                const price = Number(p.data?.price) || 0;
                if (appliedFilters.minPrice && price < parseInt(appliedFilters.minPrice)) return false;
                if (appliedFilters.maxPrice && price > parseInt(appliedFilters.maxPrice)) return false;
                return true;
            });
        }

        setFilteredProperties(filtered);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        if (!term.trim() && Object.values(filters).every(v => v === '' || (Array.isArray(v) && v.length === 0))) {
            setFilteredProperties(landBase);
            return;
        }
        applyFilters(term, filters);
    };

    const handleApplyFilters = (appliedFilters) => {
        setFilters(appliedFilters);
        applyFilters(searchTerm, appliedFilters);
    };

    const handleResetAll = () => {
        setSearchTerm('');
        setFilters({ ...EMPTY_FILTERS });
        setFilteredProperties(landBase);
    };

    const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
        if (Array.isArray(value)) return count + (value.length > 0 ? 1 : 0);
        return count + (value !== '' && value != null ? 1 : 0);
    }, 0);

    if (loading) return <Loader />;
    if (error) return <Error errMessage={error} />;

    const renderCard = (property, index, isFirst) => (
        <PropertyCard
            key={`${property._id}-${index}`}
            id={property._id}
            files={property.files}
            name={property.data?.name}
            location={property.data?.location}
            rooms={property.data?.rooms}
            floors={property.data?.floors}
            area={property.data?.area}
            price={property.data?.price}
            mode="sale"
            isFirst={isFirst}
        />
    );

    return (
        <div className="fade-up property-listing-section">
            {/* En-tête de page */}
            <div className="property-page-hero">
                <span className="property-page-eyebrow">Spotfli</span>
                <h2 className="property-page-title">{t('services.land.title', 'Land')}</h2>
                <p className="property-page-subtitle">{t('services.land.description', 'Find perfect land for your needs')}</p>
                <div className="property-page-flagline" aria-hidden="true">
                    <span /><span /><span />
                </div>
            </div>

            {/* Recherche automatique + filtres */}
            <SearchBar
                onSearch={handleSearch}
                onFilterClick={() => setIsFilterModalOpen(true)}
                searchTerm={searchTerm}
                activeFilterCount={activeFilterCount}
                placeholder={t('property_page.search_placeholder')}
                filterLabel={t('property_page.filters')}
            />

            {/* Biens près de chez vous */}
            {location.loaded && !location.error && (
                <div className="nearby-properties-section">
                    <h3 className="subsection-title">{t('property_page.nearby_title')}</h3>
                    {loadingNearby ? (
                        <div className="no-nearby-message">{t('service_selector.loading_nearby')}</div>
                    ) : nearbyProperties.length > 0 ? (
                        <div className="property-listings-container nearby-grid">
                            {nearbyProperties.map((p, i) => renderCard(p, i, firstIsNearby && i === 0))}
                        </div>
                    ) : (
                        <div className="no-nearby-message">
                            <p>{t('property_page.no_nearby')}</p>
                        </div>
                    )}
                    <hr className="section-divider" />
                </div>
            )}

            {/* Compteur de résultats */}
            <div className="results-info">
                <p className="results-count">
                    <strong>{filteredProperties.length}</strong> {t('property_page.results')}
                </p>
            </div>

            <div className="property-listings-container">
                {filteredProperties.length > 0 ? (
                    filteredProperties.map((p, i) => renderCard(p, i, !firstIsNearby && i === 0))
                ) : (
                    <div className="no-properties-found">
                        <p>{t('property_page.results_empty')}</p>
                        <button className="clear-filters-btn" onClick={handleResetAll}>
                            {t('property_page.clear_filters')}
                        </button>
                    </div>
                )}
            </div>

            {/* Modale de filtres (mode propriété) */}
            <FilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                onApplyFilters={handleApplyFilters}
                filters={filters}
                mode="property"
            />
        </div>
    );
};

export default Land;
